import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import KlaroLogo from '@/components/haccp/KlaroLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailVerifyPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('loading'); // loading | valid | invalid | verified
  const [info, setInfo] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    fetch(`${BACKEND_URL}/api/registration/company-requests/lookup?token=${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json().then(d => { setInfo(d); setState('valid'); }) : setState('invalid'))
      .catch(() => setState('invalid'));
  }, [token]);

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/registration/company-requests/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Verifica non riuscita');
      setState('verified');
      toast.success('Email verificata');
    } catch (e) { toast.error(e.message); } finally { setVerifying(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="surface max-w-md w-full p-8 text-center">
        <KlaroLogo />
        {state === 'loading' && <p className="mt-6 text-sm text-[#475467]">Verifica in corso…</p>}
        {state === 'invalid' && (<>
          <h1 className="mt-6 font-heading text-2xl font-semibold text-red-600">Link non valido</h1>
          <p className="mt-2 text-sm text-[#475467]">Il link è scaduto o è già stato utilizzato.</p>
          <Button data-testid="verify-back-btn" variant="outline" className="mt-6" onClick={() => navigate('/register')}>Ricomincia registrazione</Button>
        </>)}
        {state === 'valid' && info && (<>
          <h1 className="mt-6 font-heading text-2xl font-semibold">Conferma la tua email</h1>
          <p className="mt-2 text-sm text-[#475467]">Stai verificando <strong>{info.email}</strong> per <strong>{info.company_name}</strong>.</p>
          <Button data-testid="verify-confirm-btn" variant="success" className="w-full mt-6" onClick={verify} disabled={verifying}>{verifying ? 'Verifica…' : 'Verifica ora'}</Button>
        </>)}
        {state === 'verified' && (<>
          <div className="mx-auto mt-6 w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></div>
          <h1 className="mt-4 font-heading text-2xl font-semibold text-emerald-600">Email verificata</h1>
          <p className="mt-2 text-sm text-[#475467]">Manca solo un passaggio prima di poter accedere.</p>
          <div className="mt-6 text-left space-y-2.5">
            <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3"><span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">1</span><div><p className="text-sm font-semibold text-emerald-900">Email verificata</p><p className="text-xs text-emerald-800/80">Fatto ora.</p></div></div>
            <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-3"><span className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold">2</span><div><p className="text-sm font-semibold text-amber-900">Approvazione in corso</p><p className="text-xs text-amber-800/80">Un amministratore Klaro sta esaminando la tua richiesta.</p></div></div>
            <div className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3"><span className="w-6 h-6 rounded-full bg-slate-400 text-white flex items-center justify-center text-xs font-bold">3</span><div><p className="text-sm font-semibold text-slate-800">Riceverai una seconda email</p><p className="text-xs text-slate-600">Conterrà il link per <strong>scegliere la password</strong> e attivare l'account.</p></div></div>
          </div>
          <Button data-testid="verify-login-btn" variant="outline" className="mt-6 w-full" onClick={() => navigate('/login')}>Torna al login</Button>
        </>)}
        <p className="mt-6 text-xs text-[#98A2B3]"><Link to="/login" className="hover:underline">← Torna al login</Link></p>
      </div>
    </div>
  );
}
